import AppComponent from '../base/app-component.ts';

type CodiceCassa = 'CE' | 'EC';

interface CassaState {
  loading: boolean;
  error: string | null;
  periodi: string[];
  periodoNuovo: string;
}

interface State {
  CE: CassaState;
  EC: CassaState;
  eseguendo: CodiceCassa | null;
}

interface CassaView {
  codice: CodiceCassa;
  nome: string;
  loading: boolean;
  error: string | null;
  infoBox: boolean;
  nPeriodi: number;
  hasPeriodi: boolean;
  periodiSelezionabili: string[];
  periodoNuovo: string;
  periodoPrec: string;
}

const CASSE: { codice: CodiceCassa; nome: string }[] = [
  { codice: 'CE', nome: 'Cassa Edile (CE)' },
  { codice: 'EC', nome: 'Edilcassa (EC)' },
];

/**
 * wc-confronto-setup — Setup confronto caricamenti.
 *
 * Due card (una per cassa) affiancate su desktop, impilate su mobile.
 * Carica i periodi di entrambe le casse al mount, default periodo = più recente.
 * Click su "Esegui" → POST /api/confronti → redirect a /confronti/:nomeTabella.
 */
export class ConfrontoSetup extends AppComponent {
  static override componentName = 'wc-confronto-setup';

  protected override get templateId() { return 'confronto-setup'; }

  private state: State = {
    CE: { loading: true, error: null, periodi: [], periodoNuovo: '' },
    EC: { loading: true, error: null, periodi: [], periodoNuovo: '' },
    eseguendo: null,
  };

  protected override onConnected(): void {
    this.render();
    void this.caricaTutte();
  }

  protected override buildRenderingData(): Record<string, unknown> {
    return {
      casse: CASSE.map(c => this.buildCassaView(c.codice, c.nome)),
      overlay: this.state.eseguendo !== null,
    };
  }

  protected override afterRender(): void {
    this.delegate.on('change', '[data-select="periodo-nuovo"]', (e: Event) => {
      const select = e.target as HTMLSelectElement;
      const cassa = select.dataset.cassa as CodiceCassa | undefined;
      if (!cassa) return;
      this.state[cassa].periodoNuovo = select.value;
      this.render();
    });

    this.delegate.on('click', '[data-action="esegui"]', (e: Event) => {
      const btn = (e.target as HTMLElement).closest('[data-action="esegui"]') as HTMLElement;
      const cassa = btn.dataset.cassa as CodiceCassa | undefined;
      if (!cassa) return;
      void this.esegui(cassa);
    });
  }

  private async caricaTutte(): Promise<void> {
    await Promise.all(CASSE.map(c => this.caricaCassa(c.codice)));
  }

  private async caricaCassa(cassa: CodiceCassa): Promise<void> {
    const result = await this.api.confronti.listaPeriodi(cassa);
    const slot = this.state[cassa];
    slot.loading = false;

    if (!result.ok) {
      slot.error = result.error;
      this.render();
      return;
    }

    slot.periodi = result.data.periodi;
    slot.periodoNuovo = slot.periodi.length >= 2 ? slot.periodi[0]! : '';
    this.render();
  }

  private async esegui(cassa: CodiceCassa): Promise<void> {
    const periodoNuovo = this.state[cassa].periodoNuovo;
    if (!periodoNuovo) return;

    this.state.eseguendo = cassa;
    this.render();

    const result = await this.api.confronti.calcola(cassa, periodoNuovo);
    if (!result.ok) {
      this.state.eseguendo = null;
      this.render();
      (globalThis as unknown as { Weext?: { toast: (msg: string, opts?: unknown) => void } }).Weext
        ?.toast(result.error, { type: 'danger' });
      return;
    }

    window.location.href = `/confronti/${result.data.nomeTabella}`;
  }

  private buildCassaView(codice: CodiceCassa, nome: string): CassaView {
    const { loading, error, periodi, periodoNuovo } = this.state[codice];
    const nPeriodi = periodi.length;
    const hasPeriodi = nPeriodi >= 2;
    const stabile = !loading && !error;
    return {
      codice,
      nome,
      loading,
      error,
      infoBox: stabile && !hasPeriodi,
      nPeriodi,
      hasPeriodi: stabile && hasPeriodi,
      periodiSelezionabili: hasPeriodi ? periodi.slice(0, -1) : [],
      periodoNuovo,
      periodoPrec: hasPeriodi && periodoNuovo ? periodoPrecDi(periodi, periodoNuovo) : '',
    };
  }
}

function periodoPrecDi(periodi: string[], periodoNuovo: string): string {
  const idx = periodi.indexOf(periodoNuovo);
  if (idx < 0 || idx >= periodi.length - 1) return '';
  return periodi[idx + 1]!;
}
